import React, { useState } from 'react';
import { PHOTO_UPLOAD } from '../../utils/constants';
import { formatFileSize } from '../../utils/formatters';
import { Upload, X, Camera } from 'lucide-react';

/**
 * Componente reutilizável de upload de foto (imagens apenas).
 * Gerido por estado externo ao react-hook-form.
 *
 * Props:
 * - value: File | null — o arquivo selecionado
 * - onChange: (File | null) => void — chamado quando o arquivo muda
 * - error: string — mensagem de erro externa (ex: "foto obrigatória")
 * - existingUrl: string | null — URL da foto já cadastrada (modo edição)
 * - label: string — rótulo do campo
 */
const PhotoUpload = ({ value, onChange, error, existingUrl, label = 'Foto' }) => {
  const [preview, setPreview] = useState(existingUrl || null);
  const [fileError, setFileError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError('');

    if (!file) {
      onChange(null);
      setPreview(existingUrl || null);
      return;
    }

    if (!PHOTO_UPLOAD.ALLOWED_TYPES.includes(file.type)) {
      setFileError('Tipo de arquivo não permitido. Use JPG ou PNG.');
      return;
    }

    if (file.size > PHOTO_UPLOAD.MAX_SIZE) {
      setFileError(`Arquivo muito grande. Máximo: ${formatFileSize(PHOTO_UPLOAD.MAX_SIZE)}`);
      return;
    }

    onChange(file);

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    onChange(null);
    setPreview(existingUrl || null);
    setFileError('');
  };

  const hasSelection = !!value;
  const displayError = fileError || error;

  return (
    <div>
      <label className="label">{label}</label>

      {!preview ? (
        <div className="mt-1">
          <label
            htmlFor="photo-upload"
            className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-primary-400 transition-colors"
          >
            <div className="space-y-2 text-center">
              <Camera className="mx-auto h-12 w-12 text-gray-400" />
              <div className="text-sm text-gray-600">
                <span className="font-medium text-primary-600 hover:text-primary-500">
                  Clique para enviar uma foto
                </span>
                {' '}ou arraste a imagem
              </div>
              <p className="text-xs text-gray-500">
                PNG ou JPG até {formatFileSize(PHOTO_UPLOAD.MAX_SIZE)}
              </p>
            </div>
            <input
              id="photo-upload"
              type="file"
              className="sr-only"
              accept={PHOTO_UPLOAD.ALLOWED_TYPES.join(',')}
              onChange={handleFileChange}
            />
          </label>
        </div>
      ) : (
        <div className="mt-1 border-2 border-gray-300 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src={preview}
                alt="Foto"
                className="h-20 w-20 object-cover rounded-full"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {value?.name || 'Foto atual'}
                </p>
                {value && (
                  <p className="text-xs text-gray-500">{formatFileSize(value.size)}</p>
                )}
                <label
                  htmlFor="photo-upload-replace"
                  className="mt-1 inline-flex items-center text-xs font-medium text-primary-600 hover:text-primary-500 cursor-pointer"
                >
                  <Upload className="h-3 w-3 mr-1" />
                  Trocar foto
                  <input
                    id="photo-upload-replace"
                    type="file"
                    className="sr-only"
                    accept={PHOTO_UPLOAD.ALLOWED_TYPES.join(',')}
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            </div>
            {hasSelection && (
              <button
                type="button"
                onClick={removeFile}
                className="text-red-600 hover:text-red-800"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {displayError && <p className="mt-1 text-sm text-red-600">{displayError}</p>}
    </div>
  );
};

export default PhotoUpload;
